import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { EditIcon } from '@mm/icons/AntDesignIcons';
import classnames from 'classnames';
import React, { ChangeEvent, useState } from 'react';
import { FormGroup, Label } from 'reactstrap';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import NumberInput from '../../../../../../common/components/NumberInput/NumberInput';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../common/components/PrimarySelect/PrimarySelect';
import {
  GRANULARITY,
  GranularityItem,
  intervalToGranularity,
} from '../../../../common/timeUnitsConverter';
import guardModelMapper from '../../mappers/guardModelMapper';
import './CommentGuardForm.scss';
import { colors } from '../../../../beta/styles/colors';

const MAX_TIME_VALUE = 300000;

const granularityOptions = Object.values(GRANULARITY).map((item, key) => ({
  id: key,
  name: item.value,
}));

type Props = {
  guard: PromoterModel;
  chatbotId: number;
  css?: {
    label?: string;
    input?: string;
    button?: string;
  };
  customEditIcon?: React.ReactElement;
  usedInSlideout?: boolean;
};

const getFrequencyMax = (amount: number | undefined) => {
  if (amount && amount > 0) {
    const max = MAX_TIME_VALUE / amount;

    return Math.floor(max);
  }

  return MAX_TIME_VALUE;
};

const FrequencyCapControl = ({
  guard,
  chatbotId,
  css,
  customEditIcon,
  usedInSlideout,
}: Props) => {
  const granularInterval = intervalToGranularity(
    guard.delayIntervalInMin || 60,
  );

  const [editing, setEditing] = useState(false);
  const [counter, setCounter] = useState(granularInterval.counter);
  const [granularity, setGranularity] = useState<GranularityItem>(
    granularInterval.granularity,
  );
  const foundGranularity = granularityOptions.find(
    g => g.name === granularInterval.granularity.value,
  );
  const [selectedGranularity, setSelectedGranularity] =
    useState(foundGranularity);

  const { openGeneralTextModal } = useModalActions();

  const frequencyMax = getFrequencyMax(granularity.amount);
  const deprecatedFrequencyCapMessage = 'Deprecated Frequency Cap Message';
  const isCommentGuard =
    guard.type === 'instagram_comment_guard' ||
    guard.type === 'fb_comment_guard';

  const onCancel = () => {
    setCounter(granularInterval.counter);
    setGranularity(granularInterval.granularity);
    setSelectedGranularity(foundGranularity);
    setEditing(false);
  };

  const promoterCRUDActions = usePromoterCRUDActions();
  const updateGuard = () => {
    const delayIntervalInMin = counter * granularity.amount;

    if (delayIntervalInMin !== guard.delayIntervalInMin) {
      const nextGuard = {
        ...guard,
        delayIntervalInMin,
        message: deprecatedFrequencyCapMessage,
      };
      const promoter = guardModelMapper(nextGuard, chatbotId);

      promoterCRUDActions.update(chatbotId, promoter);
    }
  };

  const onGranularityChange = (value: DropdownOption) => {
    setCounter(1);
    const newGranularity = value?.name?.toUpperCase();
    if (newGranularity) {
      setGranularity(GRANULARITY[newGranularity]);
      const foundGranularity = granularityOptions.find(
        g => g.name === value.name,
      );
      setSelectedGranularity(foundGranularity);
    }
  };

  const onFrequencyCapChange = () => {
    openGeneralTextModal({
      header: 'Important Note: please read',
      message: (
        <span>
          Changes to the frequency cap will apply{' '}
          <span style={{ fontWeight: 'bold' }}>
            {isCommentGuard ? 'to new comments only.' : 'to new messages only.'}
          </span>{' '}
          All
          {isCommentGuard ? ' comments' : ' messages'} made before this change
          will be subject to the old frequency cap.
        </span>
      ),
      closeButtonText: 'Ok',
      onClose: updateGuard,
    });
  };

  const numberInputClassList = classnames(
    'comment-guard-form__fc__item',
    editing
      ? 'comment-guard-form__fc__counter--editing'
      : 'comment-guard-form__fc__counter',
  );

  return (
    <FormGroup className="edit-comment-guard-panel__input-container">
      <Label
        for="frequency-capping"
        className={classnames(css?.label, 'edit-comment-guard-panel__title')}
      >
        Frequency Cap
      </Label>
      <div
        className={
          editing && usedInSlideout
            ? 'comment-guard-form__fc__container__slideout--editing'
            : 'comment-guard-form__fc__container'
        }
      >
        If user posts more than one comment, only send one message every:
        <div
          className={
            editing && usedInSlideout
              ? 'comment-guard-form__fc__period__editing'
              : 'comment-guard-form__fc__period'
          }
        >
          <div
            className={
              editing
                ? 'comment-guard-form__fc__inputs-container--editing'
                : 'comment-guard-form__fc__inputs-container'
            }
          >
            <NumberInput
              className={classnames(numberInputClassList, css?.input)}
              value={counter}
              type="number"
              max={frequencyMax}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setCounter(parseInt(e.target.value.slice(0, 5), 10));
              }}
              disabled={!editing}
            />
            <PrimarySelect
              className={classnames(
                'comment-guard-form__fc__granularity',
                css?.input,
              )}
              onChange={onGranularityChange}
              disabled={!editing}
              options={granularityOptions}
              value={selectedGranularity}
              customOptionsPosition={usedInSlideout ? 'top' : 'bottom'}
            />
          </div>
          {editing ? (
            <>
              <PrimaryButton
                className={classnames(
                  editing && usedInSlideout
                    ? 'comment-guard-form__fc__button__editing'
                    : 'comment-guard-form__fc__button',
                  css?.button,
                )}
                onClick={() => {
                  setEditing(false);
                  onFrequencyCapChange();
                }}
              >
                OK
              </PrimaryButton>
              <PrimaryButton
                color={ButtonColor.invertedRed}
                className={classnames(
                  editing && usedInSlideout
                    ? 'comment-guard-form__fc__button__editing'
                    : 'comment-guard-form__fc__button',
                  css?.button,
                )}
                onClick={onCancel}
              >
                Cancel
              </PrimaryButton>
            </>
          ) : (
            <span
              onClick={() => setEditing(true)}
              className="comment-guard-form__fc__action"
            >
              {customEditIcon ? (
                customEditIcon
              ) : usedInSlideout ? (
                <EditIcon color={colors.darkGray} size={22} />
              ) : (
                <i className="fas fa-pencil-alt" />
              )}
            </span>
          )}
        </div>
      </div>
    </FormGroup>
  );
};

export default FrequencyCapControl;
